import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, faExpand, faCompress, faList
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import { QuestionnaireTemplate, Question } from '../../types/questionnaire';
import '../../styles/richtext.css';

interface PreviewModalProps {
  template: QuestionnaireTemplate;
  language: 'de' | 'en';
  onClose: () => void;
}

// **FIXED QUESTION RENDERER WITH PROPER TYPES**
const SimpleQuestion = ({ question, number, language }: { 
  question: Question; 
  number: number; 
  language: 'de' | 'en';
}) => {
  if (question.type === 'section') {
    return (
      <div className="mt-6 mb-4 first:mt-0">
        <h3 className="text-lg font-semibold text-gray-900 border-b-2 border-blue-500 pb-2 mb-3">
          {question.label[language]}
        </h3>
      </div>
    );
  }

  // **HELPER FUNCTION FOR OPTION RENDERING**
  const renderOption = (option: any, index: number) => {
    if (typeof option === 'string') {
      return option;
    }
    if (option && typeof option === 'object' && option.label) {
      return option.label[language] || option.label.de || option.label.en || option.value || 'Option';
    }
    return `Option ${index + 1}`;
  };

  return (
    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
      {/* Für info_text und admin_file keine Label-Zeile anzeigen */}
      {!['info_text', 'admin_file'].includes(question.type) && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <span className="inline-flex items-center">
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full mr-2 font-semibold">
              #{number}
            </span>
            <span>{question.label[language]}</span>
            {question.required && <span className="text-red-500 ml-1">*</span>}
          </span>
        </label>
      )}
      
      <div className="mt-2">
        {/* TEXT INPUT */}
        {question.type === 'text' && (
          <input
            type="text"
            placeholder={question.placeholder?.[language] || 'Antwort eingeben...'}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
            disabled
          />
        )}
        
        {/* TEXTAREA */}
        {question.type === 'textarea' && (
          <textarea
            placeholder={question.placeholder?.[language] || 'Antwort eingeben...'}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white resize-none"
            disabled
          />
        )}
        
        {/* YES/NO RADIO - only if no options provided */}
        {question.type === 'checkbox' && (!question.options || question.options.length === 0) && (
          <div className="flex space-x-6">
            <label className="flex items-center">
              <input type="radio" className="mr-2" disabled />
              <span className="text-sm font-medium text-green-700">Ja</span>
            </label>
            <label className="flex items-center">
              <input type="radio" className="mr-2" disabled />
              <span className="text-sm font-medium text-red-700">Nein</span>
            </label>
          </div>
        )}

        {/* CHECKBOX - MULTIPLE CHOICE - only if options provided */}
        {question.type === 'checkbox' && question.options && question.options.length > 0 && (
          <div className="space-y-2">
            {question.options && question.options.length > 0 ? (
              question.options.map((option, index) => (
                <div key={index}>
                  <label className="flex items-center">
                    <input 
                      type="checkbox" 
                      className="mr-2 h-4 w-4 text-blue-600 rounded" 
                      disabled 
                    />
                    <span className="text-sm text-gray-700">{renderOption(option, index)}</span>
                  </label>
                  {/* Textfeld für "Anderes:" Option */}
                  {option.isOther && (
                    <div className="ml-6 mt-1">
                      <input
                        type="text"
                        placeholder="Bitte spezifizieren..."
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-white"
                        disabled
                      />
                    </div>
                  )}
                </div>
              ))
            ) : null}
          </div>
        )}

        {/* RADIO - SINGLE CHOICE */}
        {question.type === 'radio' && (
          <div className="space-y-2">
            {question.options && question.options.length > 0 ? (
              question.options.map((option, index) => (
                <div key={index}>
                  <label className="flex items-center">
                    <input 
                      type="radio" 
                      name={question.id}
                      className="mr-2 h-4 w-4 text-blue-600" 
                      disabled 
                    />
                    <span className="text-sm text-gray-700">{renderOption(option, index)}</span>
                  </label>
                  {/* Textfeld für "Anderes:" Option */}
                  {option.isOther && (
                    <div className="ml-6 mt-1">
                      <input
                        type="text"
                        placeholder="Bitte spezifizieren..."
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-white"
                        disabled
                      />
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-500 italic">
                Keine Optionen definiert
              </div>
            )}
          </div>
        )}

        {/* SELECT DROPDOWN */}
        {(question.type === 'select' || question.type === 'multiselect') && (
          <div>
            <select 
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white" 
              disabled
              multiple={question.type === 'multiselect'}
            >
              <option value="">Bitte wählen...</option>
              {question.options && question.options.length > 0 ? (
                question.options.map((option, index) => (
                  <option key={index} value={typeof option === 'string' ? option : option.value || `option-${index}`}>
                    {renderOption(option, index)}
                  </option>
                ))
              ) : (
                <option value="">Keine Optionen definiert</option>
              )}
            </select>
            {/* Zeige Textfeld für "Anderes:" wenn eine solche Option existiert */}
            {question.options && question.options.some(option => option.isOther) && (
              <div className="mt-2">
                <input
                  type="text"
                  placeholder="Falls 'Anderes' gewählt: Bitte spezifizieren..."
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-white"
                  disabled
                />
              </div>
            )}
          </div>
        )}

        {/* DATE INPUT */}
        {question.type === 'date' && (
          <input
            type="date"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
            disabled
          />
        )}

        {/* FILE UPLOAD */}
        {question.type === 'file' && (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
            <div className="text-sm text-gray-500">
              📁 Datei hochladen
            </div>
            <div className="text-xs text-gray-400 mt-1">
              Drag & Drop oder klicken zum Auswählen
            </div>
          </div>
        )}

        {/* SIGNATURE */}
        {question.type === 'signature' && (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
            <div className="text-sm text-gray-500">
              ✍️ Digitale Unterschrift
            </div>
            <div className="text-xs text-gray-400 mt-1">
              Hier unterschreiben
            </div>
          </div>
        )}
        
        {/* INFO TEXT */}
        {question.type === 'info_text' && (
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start space-x-2">
              <div className="flex-shrink-0 mt-0.5">
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-semibold mr-2">
                  #{number}
                </span>
              </div>
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                {(() => {
                  // Für info_text Fragen: Nur das label verwenden (das ist der eigentliche Inhalt)
                  const labelText = question.label?.[language] || question.label?.de || '';
                  const infoText = labelText || 'Kein Informationstext verfügbar';
                  
                  // Prüfe ob der Text HTML-Tags enthält (von Rich Text Editor)
                  const containsHTML = /<[^>]*>/.test(infoText);
                  
                  if (containsHTML) {
                    return (
                      <div 
                        className="text-sm text-blue-800 leading-relaxed rich-text-content preview-rich-text"
                        dangerouslySetInnerHTML={{ __html: infoText }}
                        style={{ 
                          wordBreak: 'break-word'
                        }}
                      />
                    );
                  } else {
                    return (
                      <div className="text-sm text-blue-800 whitespace-pre-wrap leading-relaxed">
                        {infoText}
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          </div>
        )}
        
        {/* ADMIN FILE */}
        {question.type === 'admin_file' && (
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-start space-x-2 mb-3">
              <div className="flex-shrink-0 mt-0.5">
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-semibold mr-2">
                  #{number}
                </span>
              </div>
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-green-800 mb-1">
                  {question.label[language]} - Verfügbare Dateien zum Download
                </h4>
                <p className="text-xs text-green-700">
                  Diese Dateien können heruntergeladen werden.
                </p>
              </div>
            </div>
            
            {(question as any).adminFiles && (question as any).adminFiles.length > 0 ? (
              <div className="space-y-2">
                {(question as any).adminFiles.map((file: any, index: number) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                    <span className="text-sm text-gray-700">{file.originalName}</span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                      Download
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500 italic">
                Keine Dateien verfügbar
              </div>
            )}
          </div>
        )}

        {/* UNKNOWN FIELD TYPES */}
        {!['text', 'textarea', 'checkbox', 'radio', 'select', 'multiselect', 'date', 'file', 'signature', 'section', 'info_text', 'admin_file'].includes(question.type) && (
          <div className="text-sm text-gray-500 italic">
            {question.type}-Feld (Typ nicht unterstützt)
          </div>
        )}
      </div>
    </div>
  );
};

const PreviewModal: React.FC<PreviewModalProps> = ({ template, language, onClose }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);

  // Stelle sicher, dass die Rich Text Styles geladen sind
  React.useEffect(() => {
    const styleId = 'preview-rich-text-styles';
    let styleElement = document.getElementById(styleId) as HTMLStyleElement;
    
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent = `
        .preview-rich-text ul {
          list-style-type: disc !important;
          margin: 0.5rem 0 !important;
          padding-left: 1.5rem !important;
          list-style-position: outside !important;
        }
        
        .preview-rich-text ol {
          list-style-type: decimal !important;
          margin: 0.5rem 0 !important;
          padding-left: 1.5rem !important;
          list-style-position: outside !important;
        }
        
        .preview-rich-text li {
          display: list-item !important;
          margin-bottom: 0.25rem !important;
        }
        
        .preview-rich-text p {
          margin-bottom: 0.5rem !important;
        }
        
        .preview-rich-text p:last-child {
          margin-bottom: 0 !important;
        }
        
        .preview-rich-text strong {
          font-weight: 600 !important;
        }
        
        .preview-rich-text em {
          font-style: italic !important;
        }
      `;
      document.head.appendChild(styleElement);
    }
  }, []);

  // **FIXED: MEMOIZED PROCESSING - NO MUTATION!**
  const { questionsWithNumbers, stats } = useMemo(() => {
    console.log('🔥 Processing questions...');
    
    // Create NEW array (don't mutate original!)
    const sortedQuestions = [...template.questions].sort((a, b) => a.order - b.order);
    
    let questionNumber = 0;
    const questionsWithNumbers = sortedQuestions.map(question => {
      if (question.type !== 'section') {
        questionNumber++;
        return { ...question, displayNumber: questionNumber };
      }
      return { ...question, displayNumber: 0 };
    });

    // Calculate stats ONCE
    const nonSectionQuestions = sortedQuestions.filter(q => q.type !== 'section');
    const stats = {
      total: nonSectionQuestions.length,
      required: nonSectionQuestions.filter(q => q.required).length,
      sections: sortedQuestions.filter(q => q.type === 'section').length
    };

    return { questionsWithNumbers, stats };
  }, [template.questions]); // Only recalculate when questions change!

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.01 }}  // **ULTRA FAST**
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${isFullscreen ? 'p-0' : 'p-4'}`}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.99 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.99 }}
        transition={{ duration: 0.02 }}  // **ULTRA FAST**
        className={`bg-white shadow-2xl overflow-hidden ${
          isFullscreen 
            ? 'w-full h-full rounded-none' 
            : 'rounded-lg max-w-6xl w-full h-[90vh]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* **SIMPLIFIED HEADER** */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50 h-16">
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-gray-900 truncate">
              Vorschau: {template.title[language]}
            </h2>
            <div className="flex items-center gap-3 text-xs text-gray-600">
              <span>Version {template.version}</span>
              <span>{stats.total} Fragen</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 ml-4">
            <button 
              onClick={() => setShowSidebar(!showSidebar)}
              className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-md font-medium"
            >
              <FontAwesomeIcon icon={faList} className="mr-1" />
              Info
            </button>
            <button 
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="px-3 py-1.5 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md font-medium"
            >
              <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} className="mr-1" />
              {isFullscreen ? 'Normal' : 'Vollbild'}
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
        
        {/* **CONTENT** */}
        <div className={`flex ${isFullscreen ? 'h-[calc(100vh-64px)]' : 'h-[calc(90vh-64px)]'}`}>
          {/* **MAIN CONTENT** */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6">
              <div className="space-y-4">
                {questionsWithNumbers.map((question) => (
                  <SimpleQuestion
                    key={question.id}
                    question={question}
                    number={question.displayNumber}
                    language={language}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* **SIMPLE SIDEBAR** */}
          {showSidebar && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 280, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.05 }}  // **FASTER**
              className="border-l border-gray-200 bg-gray-50 overflow-hidden"
            >
              <div className="p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">
                  <FontAwesomeIcon icon={faList} className="mr-2" />
                  Statistiken
                </h3>
                
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between items-center p-2 bg-white rounded">
                    <span className="text-gray-600">Fragen gesamt:</span>
                    <span className="font-semibold text-blue-600">{stats.total}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-white rounded">
                    <span className="text-gray-600">Pflichtfragen:</span>
                    <span className="font-semibold text-red-600">{stats.required}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-white rounded">
                    <span className="text-gray-600">Sektionen:</span>
                    <span className="font-semibold text-green-600">{stats.sections}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PreviewModal;